import React, { useState, useRef, useEffect } from 'react';

interface TooltipProps {
  content: string;
  children: React.ReactNode;
  disabled?: boolean;
  maxLength?: number; // Only show tooltip if content is longer than this
}

const Tooltip: React.FC<TooltipProps> = ({ content, children, disabled = false, maxLength = 20 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isVisible && tooltipRef.current && triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      
      // Calculate position to center tooltip above the trigger element
      let newX = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
      let newY = triggerRect.top - tooltipRect.height - 8;
      
      // Adjust if tooltip would go off screen horizontally
      const padding = 8;
      const viewportWidth = window.innerWidth;
      
      if (newX < padding) {
        newX = padding;
      } else if (newX + tooltipRect.width > viewportWidth - padding) {
        newX = viewportWidth - tooltipRect.width - padding;
      }
      
      // Adjust if tooltip would go off screen vertically
      if (newY < padding) {
        // Show below element instead
        newY = triggerRect.bottom + 8;
      }
      
      setPosition({ x: newX, y: newY });
    }
  }, [isVisible]);

  const handleMouseEnter = () => {
    if (!disabled && content && content !== '-' && content.length > maxLength) {
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  const handleClick = () => {
    if (!disabled && content && content !== '-' && content.length > maxLength) {
      // Toggle tooltip on click (desktop) or show once (mobile)
      setIsVisible(!isVisible);
    }
  };

  // Hide tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        const clickElement = event.target as Element;
        // Don't hide if clicking on the original trigger element
        if (!triggerRef.current?.contains(clickElement)) {
          setIsVisible(false);
        }
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isVisible]);

  // Don't render tooltip functionality if content is empty, just a dash, or too short
  if (!content || content === '-' || content.length <= maxLength) {
    return <div>{children}</div>;
  }

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        className="cursor-pointer"
      >
        {children}
      </div>
      
      {isVisible && (
        <div
          ref={tooltipRef}
          className="fixed z-50 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg max-w-md break-words pointer-events-none"
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
          }}
        >
          {content}
          {/* Tooltip arrow - positioned dynamically */}
          <div 
            className="absolute w-0 h-0 border-l-4 border-r-4 border-transparent"
            style={{
              ...(position.y < 100 ? {
                // Arrow pointing up (tooltip below element)
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                borderBottom: '4px solid #1f2937'
              } : {
                // Arrow pointing down (tooltip above element)
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                borderTop: '4px solid #1f2937'
              })
            }}
          />
        </div>
      )}
    </>
  );
};

export default Tooltip;
